import { onErrorHandler } from '@shared/utils/errorHandlers.js';
import api from '@shared/services/api.js';

export async function getRequestorGroups() {
  try {
    return await api.get('/pe/api/exception-management/requestor-groups/');
  } catch (error) {
    onErrorHandler(error, 'exception-mgmt-getRequestorGroups');
  }
}

export async function getRequestorGroupById(id) {
  try {
    return await api.get(`/pe/api/exception-management/requestor-group/${id}`);
  } catch (error) {
    onErrorHandler(error, 'exception-mgmt-getRequestorGroupById');
  }
}

export async function createRequestorGroup(requestorGroup) {
  try {
    return await api.post(
      '/pe/api/exception-management/requestor-groups/',
      requestorGroup,
    );
  } catch (error) {
    onErrorHandler(error, 'exception-mgmt-createRequestorGroup');
  }
}

export async function updateRequestorGroup(requestorGroup) {
  try {
    return await api.patch(
      `/pe/api/exception-management/requestor-group/${requestorGroup.id}/`,
      requestorGroup,
    );
  } catch (error) {
    onErrorHandler(error, 'exception-mgmt-updateRequestorGroup');
  }
}

export async function deleteRequestorGroup(requestorGroupId) {
  try {
    return await api.delete(
      `/pe/api/exception-management/requestor-group/${requestorGroupId}/`,
    );
  } catch (error) {
    onErrorHandler(error, 'exception-mgmt-deleteRequestorGroup');
  }
}

export async function getLoanOfficers() {
  try {
    const { loan_officers } = await api.get(
      '/pe/api/exception-management/loan-officers/',
    );
    return loan_officers;
  } catch (error) {
    onErrorHandler(error, 'exception-mgmt-getLoanOfficers');
  }
}

export async function getApprovalGroups() {
  try {
    return await api.get('/pe/api/exception-management/approval-groups/');
  } catch (error) {
    onErrorHandler(error, 'exception-mgmt-getApprovalGroups');
  }
}

export async function getApprovalGroupById(id) {
  try {
    return await api.get(`/pe/api/exception-management/approval-group/${id}`);
  } catch (error) {
    onErrorHandler(error, 'exception-mgmt-getApprovalGroupById');
  }
}

export async function createApprovalGroup(approvalGroup) {
  try {
    return await api.post(
      '/pe/api/exception-management/approval-groups/',
      approvalGroup,
    );
  } catch (error) {
    onErrorHandler(error, 'exception-mgmt-createApprovalGroup');
  }
}

export async function updateApprovalGroup(approvalGroup) {
  try {
    return await api.patch(
      `/pe/api/exception-management/approval-group/${approvalGroup.id}/`,
      approvalGroup,
    );
  } catch (error) {
    onErrorHandler(error, 'exception-mgmt-updateApprovalGroup');
  }
}

export async function getApprovers(startAt = null, endAt = null) {
  try {
    let url = '/pe/api/exception-management/approvers/';
    const params = new URLSearchParams();

    if (startAt) {
      params.append('startAt', startAt);
    }
    if (endAt) {
      params.append('endAt', endAt);
    }

    if (params.toString()) {
      url += `?${params.toString()}`;
    }

    const { approvers } = await api.get(url);
    return approvers;
  } catch (error) {
    onErrorHandler(error, 'exception-mgmt-getApprovers');
  }
}

export async function cloneApproverGroup(approverGroupId) {
  try {
    const { approvalGroupId } = await api.post(
      `/pe/api/exception-management/approval-group/${approverGroupId}/clone/`,
    );
    return approvalGroupId;
  } catch (error) {
    onErrorHandler(error, 'exception-mgmt-cloneApproverGroup');
  }
}

export async function deleteApproverGroup(approverGroupId) {
  try {
    return await api.delete(
      `/pe/api/exception-management/approval-group/${approverGroupId}/`,
    );
  } catch (error) {
    onErrorHandler(error, 'exception-mgmt-deleteApproverGroup');
  }
}

export async function getSequences() {
  try {
    return await api.get(`/pe/api/exception-management/sequences/`);
  } catch (error) {
    onErrorHandler(error, 'exception-mgmt-getSequence');
  }
}

export async function getPoliciesByApprovalGroupId(id) {
  try {
    return await api.get(
      `/pe/api/exception-management/approval-group/${id}/policies`,
    );
  } catch (error) {
    onErrorHandler(error, 'exception-mgmt-getPolicy');
  }
}

export async function getPoliciesByRequestorGroupId(id) {
  try {
    return await api.get(
      `/pe/api/exception-management/requestor-group/${id}/policies`,
    );
  } catch (error) {
    onErrorHandler(error, 'exception-mgmt-getPoliciesById');
  }
}

export async function getAvailableDelegates(
  userId,
  startAt = null,
  endAt = null,
) {
  try {
    let url = `/pe/api/exception-management/approval-delegations/user/${userId}/available-delegates/`;
    const params = new URLSearchParams();
    if (startAt) params.append('startAt', startAt);
    if (endAt) params.append('endAt', endAt);
    if (params.toString()) url += `?${params.toString()}`;
    const { delegates } = await api.get(url);
    return delegates;
  } catch (error) {
    onErrorHandler(error, 'exception-mgmt-getAvailableDelegates');
  }
}

export async function getUserDelegations(userId) {
  const url = `/pe/api/exception-management/approval-delegations/user/${userId}/`;
  try {
    return await api.get(url);
  } catch (error) {
    onErrorHandler(error, 'exception-mgmt-getUserDelegations');
  }
}

export async function createUserDelegation(userId, delegationData) {
  const url = `/pe/api/exception-management/approval-delegations/user/${userId}/`;
  try {
    return await api.post(url, delegationData);
  } catch (error) {
    onErrorHandler(error, 'exception-mgmt-createUserDelegation');
  }
}

export async function editDelegation(delegationId, delegationData) {
  const url = `/pe/api/exception-management/approval-delegations/delegation/${delegationId}/`;
  try {
    return await api.patch(url, delegationData);
  } catch (error) {
    onErrorHandler(error, 'exception-mgmt-editDelegation');
  }
}

export async function cancelDelegation(delegationId) {
  const url = `/pe/api/exception-management/approval-delegations/delegation/${delegationId}/cancel/`;
  try {
    return await api.post(url);
  } catch (error) {
    onErrorHandler(error, 'exception-mgmt-cancelDelegation');
  }
}

export async function getAllActiveDelegations() {
  const url = '/pe/api/exception-management/approval-delegations/all-active/';
  try {
    return await api.get(url);
  } catch (error) {
    onErrorHandler(error, 'exception-mgmt-getAllActiveDelegations');
  }
}
